//@flow
import { getUserEmail } from "./UserInfo";

const kWispformGtagID = "GTM-WLT42B5";
const kConversionTimeoutMs = 2000;
// Google's callback can fire before the Ads requests are sent (measured ~15ms
// vs. requests starting up to ~130ms later), and a full-page navigation cancels
// them. Wait this long after the callback before letting callers navigate.
const kNavigationGraceMs = 1000;

function initGoogleTagManagerWithIDFactory() {
  let initialized: boolean = false;
  return (gtmContainerID: string) => {
    if (!initialized) {
      addGTMScriptToHeader(gtmContainerID);
      addGTMFrameToBody(gtmContainerID);
      initialized = true;
    }
  };
}

function addGTMScriptToHeader(gtmContainerID: string) {
  var script = document.createElement("script");
  script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer','${String(gtmContainerID).trim()}');`;
  document.getElementsByTagName("head")[0].appendChild(script);
}

function addGTMFrameToBody(gtmContainerID: string) {
  var noscript = document.createElement("noscript");
  var iframe = document.createElement("iframe");
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmContainerID}`;
  iframe.height = "0";
  iframe.width = "0";
  iframe.style.display = "none";
  iframe.style.visibility = "hidden";
  noscript.appendChild(iframe);
  document.getElementsByTagName("body")[0].appendChild(noscript);
}

function sha256Hex(text: string): Promise<string> {
  return window.crypto.subtle
    .digest("SHA-256", new TextEncoder().encode(text))
    .then(digest =>
      Array.from(new Uint8Array(digest))
        .map(byte => byte.toString(16).padStart(2, "0"))
        .join("")
    );
}

// Google's normalization for hashed emails: trim and lowercase, and drop dots
// from the local part of gmail.com / googlemail.com addresses.
export function normalizeEmailForGoogleAds(email: ?string): string {
  const normalized = String(email || "")
    .trim()
    .toLowerCase();
  const at = normalized.lastIndexOf("@");
  if (at <= 0 || at === normalized.length - 1) return "";
  const local = normalized.slice(0, at);
  const domain = normalized.slice(at + 1);
  if (domain === "gmail.com" || domain === "googlemail.com") {
    return local.replace(/\./g, "") + "@" + domain;
  }
  return normalized;
}

export function gtagReportPurchaseConversion(
  checkoutSessionId: string,
  onComplete: () => void = () => {}
): () => void {
  let completed = false;
  let graceTimeout = null;
  const cancel = () => {
    completed = true;
    window.clearTimeout(fallbackTimeout);
    window.clearTimeout(graceTimeout);
  };
  const complete = () => {
    if (completed) return;
    cancel();
    onComplete();
  };
  // Google may call back more than once (once per loaded container).
  const completeAfterGrace = () => {
    if (completed || graceTimeout !== null) return;
    window.clearTimeout(fallbackTimeout);
    graceTimeout = window.setTimeout(complete, kNavigationGraceMs);
  };
  // gtag can exist as a queue even if Google's script never finishes loading.
  const fallbackTimeout = window.setTimeout(complete, kConversionTimeoutMs);

  try {
    if (!checkoutSessionId || typeof window.gtag !== "function") {
      complete();
      return cancel;
    }
    let email = "";
    try {
      email = normalizeEmailForGoogleAds(getUserEmail());
    } catch (error) {
      // Missing or unreadable user info only loses enhanced matching.
    }
    // Google Ads allows 64 characters. Hash the full session ID so repeated
    // visits share an ID without truncating potentially longer Stripe IDs.
    Promise.all([
      sha256Hex(checkoutSessionId),
      email ? sha256Hex(email) : Promise.resolve(null)
    ])
      .then(([transactionId, hashedEmail]) => {
        if (completed) return;
        if (hashedEmail) {
          // Enhanced conversions: lets Google match the purchase to the ad
          // click across devices or after the click cookie is gone.
          window.gtag("set", "user_data", {
            sha256_email_address: hashedEmail
          });
        }
        window.gtag("event", "conversion", {
          send_to: "AW-11492341784/TIZYCP_z0fYZEJjw_Ocq",
          value: 100,
          currency: "NZD",
          transaction_id: transactionId,
          event_callback: completeAfterGrace,
          event_timeout: kConversionTimeoutMs
        });
      })
      .catch(complete);
  } catch (error) {
    complete();
  }
  return cancel;
}

export function initGoogleTagManagerWithWispformID() {
  const factory = initGoogleTagManagerWithIDFactory();
  factory(kWispformGtagID);
}

export default initGoogleTagManagerWithIDFactory();
